import React, { useEffect, useState } from "react";
import { api, formatApiError, BACKEND } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogDescription
} from "@/components/ui/dialog";
import { Plus, Copy, RefreshCw, Terminal, Server, Trash2, Pencil, ArrowRightLeft, AlertTriangle } from "lucide-react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

function AgentStatusBadge({ lastSeen, enabled }) {
  if (!enabled) return <span className="status-badge muted">devre dışı</span>;
  if (!lastSeen) return <span className="status-badge warn">kurulmadı</span>;
  const diff = (Date.now() - new Date(lastSeen).getTime()) / 1000;
  if (diff < 90) return <span className="status-badge ok">çevrimiçi</span>;
  if (diff < 300) return <span className="status-badge warn">gecikmeli</span>;
  return <span className="status-badge err">çevrimdışı</span>;
}

export default function WhmServers() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [installFor, setInstallFor] = useState(null);
  const emptyForm = { name: "", hostname: "", whm_api_token: "", whm_api_user: "root", notes: "", enabled: true };
  const [form, setForm] = useState(emptyForm);

  // Delete confirmation
  const [deleteFor, setDeleteFor] = useState(null);
  const [deleteZoneCount, setDeleteZoneCount] = useState(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [deleteTransferId, setDeleteTransferId] = useState(""); // optional target
  const [deleteBusy, setDeleteBusy] = useState(false);

  // Transfer zones
  const [transferFrom, setTransferFrom] = useState(null);
  const [transferTargetId, setTransferTargetId] = useState("");
  const [transferZoneCount, setTransferZoneCount] = useState(null);
  const [transferBusy, setTransferBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/whm");
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  useEffect(() => {
    load();
    const iv = setInterval(load, 10000);
    return () => clearInterval(iv);
  }, []);

  const save = async () => {
    try {
      if (editing) {
        await api.patch(`/whm/${editing.id}`, form);
        toast.success("Güncellendi");
      } else {
        await api.post("/whm", form);
        toast.success("Eklendi");
      }
      setOpen(false);
      setEditing(null);
      setForm(emptyForm);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const openDelete = async (w) => {
    setDeleteFor(w);
    setDeleteConfirmText("");
    setDeleteTransferId("");
    setDeleteZoneCount(null);
    try {
      const { data } = await api.get(`/whm/${w.id}/zone-count`);
      setDeleteZoneCount(data.zone_count ?? 0);
    } catch (e) {
      setDeleteZoneCount(0);
    }
  };

  const confirmDelete = async () => {
    if (!deleteFor) return;
    if (deleteConfirmText.trim() !== deleteFor.name) {
      toast.error(`Onaylamak için sunucu adını tam olarak girin: ${deleteFor.name}`);
      return;
    }
    setDeleteBusy(true);
    try {
      const tid = deleteTransferId && deleteTransferId !== "__none__" ? deleteTransferId : "";
      const url = tid
        ? `/whm/${deleteFor.id}?transfer_to_id=${tid}`
        : `/whm/${deleteFor.id}`;
      const { data } = await api.delete(url);
      if (data.transferred_zones > 0) {
        toast.success(`WHM silindi — ${data.transferred_zones} zone ${data.transfer_target}'e aktarıldı`);
      } else if (data.orphaned_zones > 0) {
        toast.success(`WHM silindi — ${data.orphaned_zones} zone sahipsiz kaldı`);
      } else {
        toast.success("WHM silindi");
      }
      setDeleteFor(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setDeleteBusy(false);
    }
  };

  const openTransfer = async (w) => {
    setTransferFrom(w);
    setTransferTargetId("");
    setTransferZoneCount(null);
    try {
      const { data } = await api.get(`/whm/${w.id}/zone-count`);
      setTransferZoneCount(data.zone_count ?? 0);
    } catch (e) {
      setTransferZoneCount(0);
    }
  };

  const confirmTransfer = async () => {
    if (!transferFrom) return;
    if (!transferTargetId) { toast.error("Hedef WHM seçin"); return; }
    setTransferBusy(true);
    try {
      const { data } = await api.post(`/whm/${transferFrom.id}/transfer-zones`, {
        target_whm_id: Number(transferTargetId),
      });
      toast.success(`${data.transferred} zone aktarıldı: ${data.from.name} → ${data.to.name}`);
      setTransferFrom(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setTransferBusy(false);
    }
  };

  const rotate = async (id) => {
    if (!window.confirm("Agent token yenilenecek. Mevcut agent yeni token ile yeniden kurulmalı. Devam?")) return;
    try {
      await api.post(`/whm/${id}/rotate-token`);
      toast.success("Token yenilendi");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const openEdit = (w) => {
    setEditing(w);
    setForm({
      name: w.name, hostname: w.hostname, whm_api_token: "", whm_api_user: w.whm_api_user,
      notes: w.notes || "", enabled: w.enabled,
    });
    setOpen(true);
  };

  const installCmd = (w) =>
    `curl -fsSL "${BACKEND}/api/whm/${w.id}/install.sh?token=${w.agent_token}" | bash`;

  const copy = (t) => {
    navigator.clipboard.writeText(t);
    toast.success("Kopyalandı");
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">WHM Sunucuları</h1>
          <p className="text-sm text-muted-foreground mt-1">cPanel/WHM sunucularını ve agent durumlarını yönetin</p>
        </div>
        <Button onClick={() => { setEditing(null); setForm(emptyForm); setOpen(true); }} data-testid="whm-add-btn">
          <Plus size={14} className="mr-1.5" /> Yeni Sunucu
        </Button>
      </div>

      <div className="border border-border bg-card rounded-md overflow-hidden">
        <table className="data-table" data-testid="whm-table">
          <thead>
            <tr>
              <th>Ad</th>
              <th>Hostname</th>
              <th>Agent</th>
              <th>Versiyon</th>
              <th>Son Görülme</th>
              <th>IP</th>
              <th className="text-right">İşlemler</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={7} className="text-center py-10 text-muted-foreground text-sm font-mono">henüz WHM sunucusu yok</td></tr>
            )}
            {rows.map((w) => (
              <tr key={w.id} data-testid={`whm-row-${w.id}`}>
                <td>
                  <div className="font-medium">{w.name}</div>
                  {w.notes && <div className="text-[10px] text-muted-foreground">{w.notes}</div>}
                </td>
                <td className="font-mono text-xs">{w.hostname}</td>
                <td><AgentStatusBadge lastSeen={w.agent_last_seen} enabled={w.enabled} /></td>
                <td className="font-mono text-xs">{w.agent_version || "—"}</td>
                <td className="font-mono text-xs text-muted-foreground">{w.agent_last_seen ? new Date(w.agent_last_seen).toLocaleString("tr-TR") : "—"}</td>
                <td className="font-mono text-xs">{w.agent_ip || "—"}</td>
                <td>
                  <div className="flex justify-end gap-1">
                    <Button size="sm" variant="ghost" onClick={() => setInstallFor(w)} data-testid={`whm-install-${w.id}`} title="Kurulum komutu">
                      <Terminal size={14} />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => rotate(w.id)} title="Token yenile">
                      <RefreshCw size={14} />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => openTransfer(w)} data-testid={`whm-transfer-${w.id}`} title="Zone'ları başka WHM'ye aktar" disabled={rows.length < 2}>
                      <ArrowRightLeft size={14} />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => openEdit(w)} data-testid={`whm-edit-${w.id}`} title="Düzenle">
                      <Pencil size={14} />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => openDelete(w)} data-testid={`whm-delete-${w.id}`} title="Sil" className="text-red-400 hover:text-red-300">
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="whm-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "WHM Sunucusu Düzenle" : "Yeni WHM Sunucusu"}</DialogTitle>
            <DialogDescription>cPanel/WHM sunucu bilgileri. Agent token otomatik üretilir.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Ad</Label>
              <Input data-testid="whm-form-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Sunucu-1" />
            </div>
            <div>
              <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Hostname</Label>
              <Input data-testid="whm-form-hostname" value={form.hostname} onChange={(e) => setForm({ ...form, hostname: e.target.value })} placeholder="server1.netlen.com.tr" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground">WHM API User</Label>
                <Input value={form.whm_api_user} onChange={(e) => setForm({ ...form, whm_api_user: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground">WHM API Token</Label>
                <Input type="password" value={form.whm_api_token} onChange={(e) => setForm({ ...form, whm_api_token: e.target.value })} placeholder={editing ? "(değiştirmemek için boş bırakın)" : ""} />
              </div>
            </div>
            <div>
              <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Notlar</Label>
              <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} />
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={form.enabled} onCheckedChange={(v) => setForm({ ...form, enabled: v })} data-testid="whm-form-enabled" />
              <span className="text-sm">Etkin</span>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Vazgeç</Button>
            <Button onClick={save} data-testid="whm-form-save">{editing ? "Kaydet" : "Ekle"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!installFor} onOpenChange={(v) => !v && setInstallFor(null)}>
        <DialogContent data-testid="install-dialog">
          <DialogHeader>
            <DialogTitle>Agent Kurulum Komutu</DialogTitle>
            <DialogDescription>Bu komutu WHM sunucusunda root olarak çalıştırın.</DialogDescription>
          </DialogHeader>
          {installFor && (
            <div className="space-y-3">
              <div className="border border-border rounded-md p-3 bg-background font-mono text-xs break-all">
                {installCmd(installFor)}
              </div>
              <Button onClick={() => copy(installCmd(installFor))} variant="secondary" className="w-full" data-testid="copy-install-cmd">
                <Copy size={14} className="mr-2" /> Komutu Kopyala
              </Button>
              <div className="text-[11px] text-muted-foreground leading-relaxed">
                Kurulum Python 3, <span className="font-mono text-foreground">/opt/netlen-dns-agent</span>,
                systemd unit ve WHM create/terminate hook'larını otomatik yükler.
                Agent /var/named dizinini izler ve tüm değişiklikleri panele iletir.
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Transfer zones modal */}
      <Dialog open={!!transferFrom} onOpenChange={(v) => !v && setTransferFrom(null)}>
        <DialogContent data-testid="transfer-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><ArrowRightLeft size={16} /> Zone'ları Aktar</DialogTitle>
            <DialogDescription>
              Kaynak WHM'deki tüm zone'ların sahipliği hedef WHM'ye taşınır. PDNS'lere dokunulmaz, yalnızca panel içindeki kaynak kaydı güncellenir.
            </DialogDescription>
          </DialogHeader>
          {transferFrom && (
            <div className="space-y-4">
              <div className="bg-muted/40 border border-border rounded-md p-3 text-sm font-mono">
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Kaynak:</span>
                  <span className="font-semibold">{transferFrom.name}</span>
                </div>
                <div className="flex items-center justify-between mt-1">
                  <span className="text-muted-foreground">Etkilenecek zone sayısı:</span>
                  <span className="font-semibold">{transferZoneCount === null ? "..." : transferZoneCount}</span>
                </div>
              </div>
              <div>
                <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Hedef WHM</Label>
                <Select value={transferTargetId} onValueChange={setTransferTargetId}>
                  <SelectTrigger className="mt-1" data-testid="transfer-target"><SelectValue placeholder="WHM seç..." /></SelectTrigger>
                  <SelectContent>
                    {rows.filter((w) => w.id !== transferFrom.id).map((w) => (
                      <SelectItem key={w.id} value={String(w.id)}>{w.name} · <span className="text-muted-foreground text-xs">{w.hostname}</span></SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {transferZoneCount === 0 && (
                <div className="text-[11px] text-muted-foreground font-mono">Bu WHM'de aktarılacak zone yok.</div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setTransferFrom(null)} disabled={transferBusy}>Vazgeç</Button>
            <Button onClick={confirmTransfer} disabled={transferBusy || !transferTargetId || transferZoneCount === 0} data-testid="transfer-confirm">
              {transferBusy ? "Aktarılıyor..." : `${transferZoneCount ?? ""} Zone'u Aktar`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation modal */}
      <Dialog open={!!deleteFor} onOpenChange={(v) => !v && setDeleteFor(null)}>
        <DialogContent data-testid="delete-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-400">
              <AlertTriangle size={16} /> WHM Sunucusunu Sil
            </DialogTitle>
            <DialogDescription>
              Bu işlem geri alınamaz. Zone'lar PDNS'lerden silinmez, fakat paneldeki <span className="font-mono">whm_server_id</span> alanları NULL olur (orphan).
            </DialogDescription>
          </DialogHeader>
          {deleteFor && (
            <div className="space-y-4">
              <div className="bg-red-500/10 border border-red-500/30 rounded-md p-3 text-sm">
                <div className="flex items-center justify-between font-mono">
                  <span className="text-muted-foreground">Sunucu:</span>
                  <span className="font-semibold">{deleteFor.name}</span>
                </div>
                <div className="flex items-center justify-between font-mono mt-1">
                  <span className="text-muted-foreground">Hostname:</span>
                  <span>{deleteFor.hostname}</span>
                </div>
                <div className="flex items-center justify-between font-mono mt-1">
                  <span className="text-muted-foreground">Etkilenecek zone:</span>
                  <span className={`font-semibold ${deleteZoneCount > 0 ? "text-amber-400" : ""}`}>
                    {deleteZoneCount === null ? "..." : deleteZoneCount}
                  </span>
                </div>
              </div>
              {deleteZoneCount > 0 && (
                <>
                  <div className="text-[11px] text-amber-400 font-mono leading-relaxed">
                    ⚠️ {deleteZoneCount} zone etkilenecek. İsterseniz silmeden önce başka bir WHM'ye aktarın.
                  </div>
                  <div>
                    <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground">
                      Zone'ları önce şuraya aktar <span className="text-[10px] normal-case text-muted-foreground">(opsiyonel)</span>
                    </Label>
                    <Select value={deleteTransferId} onValueChange={setDeleteTransferId}>
                      <SelectTrigger className="mt-1" data-testid="delete-transfer-target">
                        <SelectValue placeholder="Aktarma — zone'lar orphan kalacak" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">Aktarma — zone'lar orphan kalacak</SelectItem>
                        {rows.filter((w) => w.id !== deleteFor.id).map((w) => (
                          <SelectItem key={w.id} value={String(w.id)}>{w.name} · <span className="text-muted-foreground text-xs">{w.hostname}</span></SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {deleteTransferId && deleteTransferId !== "__none__" && (
                      <div className="text-[11px] text-emerald-400 font-mono mt-1.5">
                        ✓ Zone'lar silme öncesi <span className="text-foreground">{rows.find(r => String(r.id) === deleteTransferId)?.name}</span>'e aktarılacak
                      </div>
                    )}
                  </div>
                </>
              )}
              <div>
                <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground">
                  Onaylamak için sunucu adını girin: <span className="text-foreground">{deleteFor.name}</span>
                </Label>
                <Input
                  className="mt-1 font-mono"
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  placeholder={deleteFor.name}
                  data-testid="delete-confirm-input"
                  autoFocus
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDeleteFor(null)} disabled={deleteBusy}>Vazgeç</Button>
            <Button
              onClick={confirmDelete}
              disabled={deleteBusy || deleteConfirmText.trim() !== (deleteFor?.name || "")}
              className="bg-red-500 hover:bg-red-600 text-white"
              data-testid="delete-confirm"
            >
              {deleteBusy ? "Siliniyor..." : "Kalıcı Olarak Sil"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
